import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { actions as statisticActions } from 'features/statistic';

import { convertFiltersBalanceStatistic, convertFiltersStatistic } from './convertFilters';

const ARGENTINA_TIMEZONE_OFFSET = -3; // Часовой пояс Аргентины (GMT -3)
const MILLISECONDS_IN_HOUR = 60 * 60 * 1000; // Количество миллисекунд в часе
const MILLISECONDS_IN_DAY = 24 * MILLISECONDS_IN_HOUR; // Количество миллисекунд в сутках

export const EStatisticsTab = {
  agentsStatistics: 'agentsStatistics',
  playersStatistics: 'playersStatistics',
  agentsBalance: 'agentsBalance',
  playersBalance: 'playersBalance',
};

export const ETableType = {
  Statistics: 'Statistics',
  Balance: 'Balance',
};

const comparisonNamingList = {
  default: '',
  totalNetwin: 'totalNetwinByDates',
};

const useModel = () => {
  const dispatch = useDispatch();
  const { actionProcessing, statistic } = useSelector(state => state.statistic, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);

  const targetUserId = useSelector(state => state.tree.targetUserId, shallowEqual);
  const authId = useSelector(state => state.auth.id, shallowEqual);

  const [activeTab, setActiveTab] = useState(EStatisticsTab.agentsStatistics);

  const tabs = useMemo(
    () =>
      [
        {
          value: EStatisticsTab.agentsStatistics,
          title: locale.myAgents,
          active: true,
        },
        {
          value: EStatisticsTab.playersStatistics,
          title: locale.myPlayers,
          active: false,
        },
        {
          value: EStatisticsTab.playersBalance,
          title: locale.playersBalance,
          amount: statistic.totalUserBalance,
          active: false,
        },
        {
          value: EStatisticsTab.agentsBalance,
          title: locale.agentBalance,
          amount: statistic.totalAgentBalance,
          active: false,
        },
      ].map(tab => ({
        ...tab,
        active: activeTab === tab.value,
      })),
    [activeTab, statistic, locale],
  );

  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [comparisonType, setComparisonType] = useState(null);
  const [activeDaySelect, setActiveDaySelect] = useState({
    text: '',
    value: '',
    value2: '',
  });

  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setHours(0, 0, 0, 0) - MILLISECONDS_IN_DAY), // Вчера в 00:00
    to: new Date(new Date().setHours(0, 0, 0, 0)), // Сегодня в 00:00
  });

  const resetActiveDay = useCallback(() => {
    setActiveDaySelect({ text: '', value: '', value2: '' });
  }, []);
  const [isAsc, setIsAsc] = useState(false);

  const setActiveDay = useCallback(day => {
    setActiveDaySelect(day);
    setDateRange(prev => ({
      ...prev,
      from: day.value,
      to: day.value2,
    }));
  }, []);

  const onChangeFrom = useCallback(date => {
    setDateRange(prev => ({ ...prev, from: date }));
    resetActiveDay();
  }, []);

  const onChangeTo = useCallback(date => {
    setDateRange(prev => ({ ...prev, to: date }));
    resetActiveDay();
  }, []);

  const onChangeComparisonType = useCallback(
    type => {
      if (comparisonType !== type) {
        setComparisonType(type);
        setIsComparisonMode(true);
      } else {
        setIsComparisonMode(false);
        setComparisonType(comparisonNamingList.default);
      }
    },
    [comparisonType],
  );

  const fetchStatistics = useCallback(() => {
    // const fromUTC = adjustDateToUTC(dateRange.from);
    // const toUTC = adjustDateToUTC(dateRange.to);
    // toUTC.setDate(toUTC.getDate() + 1);

    if (activeTab.indexOf(ETableType.Statistics) >= 0) {
      const filters = convertFiltersStatistic({
        // from: fromUTC,
        // to: toUTC,
        to: dateRange.to,
        from: dateRange.from,
        userId: targetUserId,
      });

      if (activeTab === EStatisticsTab.playersStatistics) {
        filters.append('is_user_ranking', 'true');
      }

      dispatch(
        statisticActions.getStatistic({
          filters: {
            to: dateRange.to,
            from: dateRange.from,
            // userId: targetUserId,
            isUserRanking: activeTab === EStatisticsTab.playersStatistics ? true : false,
          },
          id: authId,
          isComparisonMode,
          comparisonType,
        }),
      );
    }

    if (activeTab.indexOf(ETableType.Balance) >= 0) {
      const filters = convertFiltersBalanceStatistic({
        // from: fromUTC,
        // to: toUTC,
        role: activeTab === EStatisticsTab.playersBalance ? 0 : 6,
        to: dateRange.to,
        from: dateRange.from,
        isAsc: isAsc,
      });

      dispatch(
        statisticActions.getBalanceStatistic({
          filters,
        }),
      );
    }
  }, [
    dateRange.from,
    dateRange.to,
    targetUserId,
    activeTab,
    authId,
    dispatch,
    isAsc,
    targetUserId,
    activeTab,
    authId,
    dispatch,
    isComparisonMode,
    comparisonType,
  ]);

  const data = useMemo(() => {
    const arr = statistic.totalMetricStatistic.currentPeriod.totalByDate || [];
    return {
      type: 'totalSum',
      data: arr?.map(t => t.count),
      time: arr?.map(t => dayjs(t.date).subtract(3, 'hour').format('DD.MM')),
      originalTime: arr.map(t => dayjs(t.date).subtract(3, 'hour').format('DD.MM')),
      color: 'rgba(177, 0, 255, 1)',
      colorFill: 'rgba(70, 21, 92, 0.6)',
      title: 'Total',
    };
  }, [statistic, comparisonType]);

  const data2 = useMemo(() => {
    if (!isComparisonMode) return [];
    const arr = statistic.totalMetricStatistic.previousPeriod.totalByDate ?? [];
    return {
      type: 'totalSumDiff',
      data: arr.map(t => t.count),
      time: arr.map(t => dayjs(t.date).subtract(3, 'hour').format('DD.MM')),
      originalTime: arr.map(t => dayjs(t.originalDate).subtract(3, 'hour').format('DD.MM')),
      color: 'rgba(67, 172, 106, 1)',
      colorFill: 'rgba(67, 172, 106, 0.45)',
      title: 'Total',
    };
  }, [statistic, isComparisonMode]);

  const buildTopItems = useCallback(
    locale => {
      const s = statistic || {};
      const cp = comparisonType;
      const ap = actionProcessing;

      return [
        {
          key: 'usersCount',
          icon: 'users',
          left: { value: s.usersCount, label: locale.users },
          compareKey: 'usersCount',
          diffPercent: s?.totalMetricStatistic?.usersCountPercent,
          diff: s?.totalMetricStatistic?.usersCount,
          showRight: !ap && cp === 'usersCount',
          isComparative: false,
        },
        {
          key: 'activeUser',
          icon: 'activeUser',
          left: { value: s.activeUser, label: locale.activeUser },
          compareKey: 'activeUser',
          diffPercent: s.totalMetricStatistic?.diffPercent,
          diff: s.totalMetricStatistic?.totalDiff,
          showRight: !ap && cp === 'activeUser',
          isComparative: true,
        },
        {
          key: 'totalBets',
          icon: 'totalBets',
          left: { value: s.totalBets, label: locale.totalBets },
          compareKey: 'totalBets',
          diffPercent: Number(s.totalMetricStatistic?.diffPercent).toFixed(2),
          diff: Number(s.totalMetricStatistic?.totalDiff).toFixed(2),
          showRight: !ap && cp === 'totalBets',
          isComparative: true,
        },
        {
          key: 'totalWin',
          icon: 'totalWin',
          left: { value: s.totalWin, label: locale.totalWin },
          compareKey: 'totalWin',
          diffPercent: Number(s.totalMetricStatistic?.diffPercent).toFixed(2),
          diff: Number(s.totalMetricStatistic?.totalDiff).toFixed(2),
          showRight: !ap && cp === 'totalWin',
          isComparative: true,
        },
        {
          key: 'totalNetwin',
          icon: 'totalNetwin',
          left: { value: s.totalNetwin, label: locale.totalNetwin },
          compareKey: 'totalNetwin',
          diffPercent: Number(s.totalMetricStatistic?.diffPercent).toFixed(2),
          diff: Number(s.totalMetricStatistic?.totalDiff).toFixed(2),
          showRight: !ap && cp === 'totalNetwin',
          isComparative: true,
        },
        {
          key: 'margin',
          icon: 'margin',
          left: { value: (s?.totalMargin ?? 0) + '%', label: locale.margin },
          compareKey: 'margin',
          diffPercent: Number(s.totalMetricStatistic?.diffPercent).toFixed(2),
          diff: Number(s.totalMetricStatistic?.totalDiff).toFixed(2),
          showRight: !ap && cp === 'margin',
          isComparative: true,
          info: {
            text: locale.margin,
            description: `${locale.margin} = ${locale.totalNetwin} / ${locale.betAmount} * 100%`,
          },
        },
      ];
    },
    [statistic, comparisonType, actionProcessing],
  );

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics, isAsc]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      fetchStatistics();
    },
    [fetchStatistics],
  );

  return {
    inputState: {
      values: dateRange,
      activeTab,
      setActiveTab,
      setActiveDay,
      setIsAsc,
      isAsc,
      tabs,
      onChangeFrom,
      onChangeTo,
      onChangeComparisonType,
      buildTopItems,
    },
    outputState: {
      onSubmit,
      data,
      data2,
      statistic,
      isComparisonMode,
      comparisonType,
      activeDaySelect,
      actionProcessing,
    },
  };
};

export default useModel;
